import React, { useState, useEffect, useCallback, useMemo } from 'react'
import {
  Layout,
  Tag,
  Button,
  Form,
  Radio,
  Select,
  TimePicker,
  Popconfirm,
  Modal
} from 'antd'
import { CloseCircleOutlined, PlusOutlined } from '@ant-design/icons'
import dayjs, { Dayjs } from 'dayjs'
import classnames from 'classnames'
import { useAppDispatch, useAppSelector } from '../../app/hooks'
import CONFIG from '../../constants/config'
import { CLOCK_IN_OPTIONS, ATTENDANCE_TYPE } from '../../constants/attendance'
import {
  fetchAttendanceSetting,
  updateAttendanceSetting
} from '../../actions/attendance'
import sensors from '../../utils/sensors'
import * as SentryUtils from '../../utils/sentryUtils'

import PageHeader from '@/components/PageHeader'
const { Content } = Layout
const { Option } = Select
const FormItem = Form.Item

// 时间规则类型
interface TimeRule {
  startTime: number | null
  endTime: number | null
  num: number
}

// 灵活考勤规则类型
interface FlexibleRule {
  attendanceType: number
  minDuration: number
  ruleTitle: string
  timeRules: TimeRule[]
}

// 固定考勤规则类型
interface FixedRule {
  attendanceType: number
  ruleTitle: string
  offworkEffectiveEnd: number
  onworkEffectiveStart: number
  timeRules: TimeRule[]
}

// 验证错误属性类型
interface ValidateErrorProps {
  validateStatus?: 'error'
  help?: string
}

const formatMinutes = (minutes: number | null) =>
  dayjs().startOf('day').minute(minutes || 0).format(CONFIG.hourMinuteFormat)
const formatDuration = (minutes: number) => {
  const hours = minutes >= 60 ? `${Math.floor(minutes / 60)}小时` : ''
  const mins = minutes % 60 > 0 ? `${minutes % 60}分钟` : ''
  return hours + mins
}
const tomorrow = (minutes: number) => (minutes < 24 * 60 ? minutes + 24 * 60 : minutes)

function Setting() {
  const dispatch = useAppDispatch()

  // local state
  const [isEdit, setIsEdit] = useState(false)
  const [ruleType, setRuleType] = useState(0)
  const [flexibleRule, setFlexibleRule] = useState<FlexibleRule>({
    attendanceType: 1,
    minDuration: 0,
    ruleTitle: '灵活考勤',
    timeRules: [
      {
        startTime: null,
        endTime: null,
        num: 0
      }
    ]
  })
  const [fixedRule, setFixedRule] = useState<FixedRule>({
    attendanceType: 2,
    ruleTitle: '固定考勤',
    offworkEffectiveEnd: 120,
    onworkEffectiveStart: 120,
    timeRules: [
      {
        startTime: null,
        endTime: null,
        num: 0
      }
    ]
  })
  const [oldRule, setOldRule] = useState<FlexibleRule | FixedRule | null>(null)

  // Redux state
  const groupId = useAppSelector((state) => state.group.groupId)
  const userId = useAppSelector((state) => state.main.user?.user_id)

  // componentDidMount
  useEffect(() => {
    dispatch(
      fetchAttendanceSetting({
        groupID: groupId,
        userID: userId
      })
    )
      .then((data) => {
        if (data.rule.attendanceType === ATTENDANCE_TYPE.FLEXIBLE) {
          setFlexibleRule(data.rule)
          setRuleType(data.rule.attendanceType)
          setOldRule(data.rule)
        } else {
          setFixedRule(data.rule)
          setRuleType(data.rule.attendanceType)
          setOldRule(data.rule)
        }
      })
      .catch(SentryUtils.capturePromiseException)
  }, [dispatch, groupId, userId])

  // 更新规则
  const handleUpdateRule = useCallback(() => {
    const currentRule =
      ruleType === ATTENDANCE_TYPE.FIXED ? fixedRule : flexibleRule
    const times = (currentRule.timeRules || []).map(
      (rule) =>
        `${formatMinutes(rule.startTime)}-${formatMinutes(rule.endTime)}`
    )
    sensors.track('web_workgroup_team_statistics_rule_set', {
      attendanceWay:
        ruleType === ATTENDANCE_TYPE.FIXED ? 'fixedTime' : 'flexibleTime',
      attendanceTime: times.join(','),
      attendanceTimeNum: (currentRule.timeRules || []).length,
      onWorkTimeLimit: 'onworkEffectiveStart' in currentRule ? currentRule.onworkEffectiveStart : undefined,
      ofWorkTimeLimit: 'offworkEffectiveEnd' in currentRule ? currentRule.offworkEffectiveEnd : undefined,
      workHoursLimit: 'minDuration' in currentRule ? currentRule.minDuration : undefined
    })
    dispatch(
      updateAttendanceSetting({
        groupID: groupId,
        userID: userId,
        rule: currentRule
      })
    )
      .then((data) => {
        window.location.reload()
      })
      .catch(SentryUtils.capturePromiseException)
  }, [dispatch, groupId, userId, ruleType, fixedRule, flexibleRule])

  // 开始时间变更
  const handleStartTimeChange = useCallback(
    (index: number, time: Dayjs | null) => {
      if (time) {
        const currentRule =
          ruleType === ATTENDANCE_TYPE.FIXED ? fixedRule : flexibleRule
        let minutes = time.diff(dayjs(time).startOf('day'), 'minutes')
        const rule = { ...currentRule.timeRules[index] }
        const firstStartTime = currentRule.timeRules[0]?.startTime ?? 0
        const prevEndTime = currentRule.timeRules[index - 1]?.endTime ?? 0
        // 如果早于第一个时间段的上班时间，则是次日
        if (
          (index !== 0 && minutes < firstStartTime) ||
          (prevEndTime && prevEndTime > 24 * 60)
        ) {
          minutes += 24 * 60
        }
        if (rule.endTime !== null) {
          // 如果已设置下班时间
          if (minutes > rule.endTime) {
            // 且 start 晚于 end
            rule.endTime = tomorrow(rule.endTime) // 则 end为次日
          }
        }
        rule.startTime = minutes
        const newTimeRules = [...currentRule.timeRules]
        newTimeRules[index] = rule
        if (ruleType === ATTENDANCE_TYPE.FIXED) {
          setFixedRule({ ...fixedRule, timeRules: newTimeRules })
        } else {
          setFlexibleRule({ ...flexibleRule, timeRules: newTimeRules })
        }
      }
    },
    [ruleType, fixedRule, flexibleRule]
  )

  // 结束时间变更
  const handleEndTimeChange = useCallback(
    (index: number, time: Dayjs | null) => {
      if (time) {
        const currentRule =
          ruleType === ATTENDANCE_TYPE.FIXED ? fixedRule : flexibleRule
        let minutes = time.diff(dayjs(time).startOf('day'), 'minutes')
        const rule = { ...currentRule.timeRules[index] }
        const firstStartTime = currentRule.timeRules[0]?.startTime ?? 0
        // 如果早于第一个时间段的上班时间 || 本时间段的上班时间为次日
        if (
          minutes < firstStartTime ||
          (rule.startTime !== null && rule.startTime > 24 * 60)
        ) {
          minutes += 24 * 60
        }
        rule.endTime = minutes
        const newTimeRules = [...currentRule.timeRules]
        newTimeRules[index] = rule
        if (ruleType === ATTENDANCE_TYPE.FIXED) {
          setFixedRule({ ...fixedRule, timeRules: newTimeRules })
        } else {
          setFlexibleRule({ ...flexibleRule, timeRules: newTimeRules })
        }
      }
    },
    [ruleType, fixedRule, flexibleRule]
  )

  // 创建验证错误属性
  const handleCreateValidateErrorProps = useCallback(
    (index: number) => {
      const currentRule =
        ruleType === ATTENDANCE_TYPE.FIXED ? fixedRule : flexibleRule
      const rule = currentRule.timeRules[index]
      const startValidateErrorProps: ValidateErrorProps = {}
      const endValidateErrorProps: ValidateErrorProps = {}
      const firstStartTime = currentRule.timeRules[0]?.startTime ?? 0
      const prevEndTime = currentRule.timeRules[index - 1]?.endTime ?? 0
      const nextStartTime = currentRule.timeRules[index + 1]?.startTime ?? null
      // startTime <= rules[index-1].endTime， 开始时间必须晚于上一时段的结束时间
      if (
        rule?.startTime !== null &&
        index > 0 &&
        rule.startTime <= prevEndTime
      ) {
        startValidateErrorProps.validateStatus = 'error'
        startValidateErrorProps.help = '时间应晚于上一个时间点'
      }
      if (rule?.startTime !== null && (rule.startTime - firstStartTime) >= 24 * 60) {
        startValidateErrorProps.validateStatus = 'error'
        startValidateErrorProps.help = '时间范围不可超过24小时'
      }
      // endTime <= startTime || endTime >= rules[index+1].startTime，结束时间必须晚于开始时间，且必须早于下一时段的结束时间
      if (
        rule?.endTime !== null && rule?.startTime !== null &&
        (rule.endTime <= rule.startTime ||
          (nextStartTime !== null && rule.endTime >= nextStartTime))
      ) {
        endValidateErrorProps.validateStatus = 'error'
        endValidateErrorProps.help = '时间范围不可重叠'
      }
      if (rule?.endTime !== null && (rule.endTime - firstStartTime) >= 24 * 60) {
        endValidateErrorProps.validateStatus = 'error'
        endValidateErrorProps.help = '时间范围不可超过24小时'
      }
      return {
        start: startValidateErrorProps,
        end: endValidateErrorProps
      }
    },
    [ruleType, fixedRule, flexibleRule]
  )

  // 检查规则
  const handleCheckRules = useCallback(() => {
    const currentRule =
      ruleType === ATTENDANCE_TYPE.FIXED ? fixedRule : flexibleRule
    let hasError = false
    const firstStartTime = currentRule.timeRules[0]?.startTime ?? 0
    ;(currentRule.timeRules || []).forEach((rule, index) => {
      const prevEndTime = currentRule.timeRules[index - 1]?.endTime ?? 0
      const nextStartTime = currentRule.timeRules[index + 1]?.startTime ?? null
      if (
        rule.startTime !== null &&
        index > 0 &&
        rule.startTime <= prevEndTime
      ) {
        hasError = true
      }
      if (rule.startTime !== null && (rule.startTime - firstStartTime) >= 24 * 60) {
        hasError = true
      }
      if (
        rule.endTime !== null && rule.startTime !== null &&
        (rule.endTime <= rule.startTime ||
          (nextStartTime !== null && rule.endTime >= nextStartTime))
      ) {
        hasError = true
      }
      if (rule.endTime !== null && (rule.endTime - firstStartTime) >= 24 * 60) {
        hasError = true
      }
    })
    if (hasError) {
      Modal.warning({
        title: '考勤规则设置有误',
        content: '考勤规则设置有误，请调整后重新提交。',
        okText: '我知道了'
      })
    } else {
      Modal.confirm({
        title: '考勤规则将立即生效',
        content: '仅针对今日及以后，之前的考勤规则不受影响。',
        onOk: handleUpdateRule
      })
    }
  }, [ruleType, fixedRule, flexibleRule, handleUpdateRule])

  // 取消
  const handleCancel = useCallback(() => {
    const currentRule =
      ruleType === ATTENDANCE_TYPE.FIXED ? fixedRule : flexibleRule
    // 深度比较规则是否改变
    const isRuleChanged =
      JSON.stringify(currentRule) !== JSON.stringify(oldRule)
    if (isRuleChanged) {
      Modal.confirm({
        title: '该设置尚未保存，是否离开？',
        content: '当前考勤设置内容尚未保存，离开后将保存原有设置。',
        onOk: () => window.location.reload()
      })
    } else {
      window.location.reload()
    }
  }, [ruleType, fixedRule, flexibleRule, oldRule])

  // 展示固定考勤规则
  const renderFixedRule = useCallback(() => {
    return (
      <div className="description">
        <h2>当前考勤规则</h2>
        <h4>固定时间上下班</h4>
        <p>说明：有固定的上下班时间，考核迟到和早退</p>
        <div className="rules">
          <div className="row row-gray">
            <h4>上下班时间</h4>
          </div>
          {fixedRule.timeRules &&
            fixedRule.timeRules.map((rule, index) => (
              <div
                className={classnames(
                  'row',
                  { 'row-gray': index % 2 === 1 },
                  { 'row-white': index % 2 === 0 }
                )}
                key={index}
              >
                <p>
                  <span>
                    上班：
                    {rule.startTime !== null && formatMinutes(rule.startTime)}
                    {rule.startTime !== null && rule.startTime >= 1440 && <Tag color="gold">次日</Tag>}
                  </span>
                  <span>~</span>
                  <span>
                    下班：
                    {rule.endTime !== null && formatMinutes(rule.endTime)}
                    {rule.endTime !== null && rule.endTime >= 1440 && <Tag color="gold">次日</Tag>}
                  </span>
                </p>
              </div>
            ))}
        </div>
        <div className="rules" style={{ marginTop: '40px' }}>
          <div className="row row-gray">
            <h4>打卡时间限制</h4>
          </div>
          <div className="row row-white">
            <p>
              <span style={{ width: '220px' }}>
                上班：
                {
                  CLOCK_IN_OPTIONS.find(
                    (option) => option.value === fixedRule.onworkEffectiveStart
                  )?.onWorkText
                }
              </span>
              <span>
                下班：
                {
                  CLOCK_IN_OPTIONS.find(
                    (option) => option.value === fixedRule.offworkEffectiveEnd
                  )?.offWorkText
                }
              </span>
            </p>
          </div>
        </div>
        <Button
          type="primary"
          style={{ float: 'right', marginTop: '40px' }}
          onClick={() => setIsEdit(true)}
        >
          修改考勤规则
        </Button>
      </div>
    )
  }, [fixedRule])

  // 展示灵活考勤规则
  const renderFlexibleRule = useCallback(() => {
    return (
      <div className="description">
        <h2>当前考勤规则</h2>
        <h4>灵活时间上下班</h4>
        <p>说明：上下班时间不固定，考核工作时长 </p>
        <div className="rules">
          <div className="row row-gray">
            <h4>有效打卡时间范围</h4>
          </div>
          {(flexibleRule.timeRules || []).map((rule, index) => (
            <div
              className={classnames(
                'row',
                { 'row-gray': index % 2 === 1 },
                { 'row-white': index % 2 === 0 }
              )}
              key={index}
            >
              <p>
                <span>
                  {formatMinutes(rule.startTime)}
                  {rule.startTime !== null && rule.startTime >= 1440 && <Tag color="gold">次日</Tag>}
                </span>
                <span>~</span>
                <span>
                  {formatMinutes(rule.endTime)}
                  {rule.endTime !== null && rule.endTime >= 1440 && <Tag color="gold">次日</Tag>}
                </span>
              </p>
            </div>
          ))}
        </div>
        <h4 className="work-hours">
          工作时长要求：
          <span>
            {flexibleRule.minDuration
              ? formatDuration(flexibleRule.minDuration)
              : '无要求'}
          </span>
        </h4>
        <p>说明：设定员工最低工作时长标准</p>
        <Button
          type="primary"
          style={{ float: 'right', marginTop: '40px' }}
          onClick={() => setIsEdit(true)}
        >
          修改考勤规则
        </Button>
      </div>
    )
  }, [flexibleRule])

  // 固定考勤设置
  const renderFixedSetting = useCallback(() => {
    const addTimeRule = () =>
      setFixedRule({
        ...fixedRule,
        timeRules: [
          ...fixedRule.timeRules,
          {
            startTime: null,
            endTime: null,
            num: fixedRule.timeRules.length + 1
          }
        ]
      })
    const deleteTimeRule = (index: number) => {
      const newTimeRules = fixedRule.timeRules.filter((_, i) => i !== index)
      setFixedRule({ ...fixedRule, timeRules: newTimeRules })
    }
    const lastRule = fixedRule.timeRules[fixedRule.timeRules.length - 1]
    return (
      <div>
        <div className="table">
          <div className="row row-gray">
            <h4>上下班时间</h4>
          </div>
          <Form layout="inline">
            {fixedRule.timeRules.map((rule, index) => {
              const validateErrorProps = handleCreateValidateErrorProps(index)
              return (
                <div
                  className={classnames(
                    'row',
                    { 'row-gray': index % 2 === 1 },
                    { 'row-white': index % 2 === 0 }
                  )}
                  key={index}
                >
                  <FormItem
                    className="rule-item"
                    label="上班"
                    {...validateErrorProps.start}
                  >
                    <TimePicker
                      placeholder="选择时间"
                      allowClear={false}
                      format={CONFIG.hourMinuteFormat}
                      value={
                        rule.startTime !== null
                          ? dayjs().startOf('day').minute(rule.startTime)
                          : null
                      }
                      onChange={(time, timeString) =>
                        handleStartTimeChange(index, time)
                      }
                    />
                    {rule.startTime !== null && rule.startTime >= 24 * 60 && <Tag color="gold">次日</Tag>}
                  </FormItem>
                  <FormItem
                    className="rule-item"
                    label="下班"
                    {...validateErrorProps.end}
                  >
                    <TimePicker
                      allowClear={false}
                      format={CONFIG.hourMinuteFormat}
                      value={
                        rule.endTime !== null
                          ? dayjs().startOf('day').minute(rule.endTime)
                          : null
                      }
                      onChange={(time) =>
                        handleEndTimeChange(index, time)
                      }
                      placeholder="选择时间"
                    />
                    {rule.endTime !== null && rule.endTime >= 24 * 60 && <Tag color="gold">次日</Tag>}
                  </FormItem>
                  {// 删除时段
                  index > 0 && (
                    <Popconfirm
                      title="是否确认删除？"
                      onConfirm={() => deleteTimeRule(index)}
                    >
                      <span className="remove">
                        <CloseCircleOutlined />
                      </span>
                    </Popconfirm>
                  )}
                </div>
              )
            })}
          </Form>
          {// 添加时段，最多设置5段时间
          fixedRule.timeRules.length < 5 && (
            <div
              className={classnames(
                'row',
                { 'row-gray': fixedRule.timeRules.length % 2 === 1 },
                { 'row-white': fixedRule.timeRules.length % 2 === 0 }
              )}
            >
              <Button
                type="link"
                onClick={addTimeRule}
                disabled={
                  lastRule?.startTime === null || lastRule?.endTime === null
                }
              >
                <PlusOutlined />
                上下班时间
              </Button>
            </div>
          )}
        </div>
        <div className="table">
          <div className="row row-gray">
            <h4>
              打卡时间限制：
              <span className="rule-item" style={{ width: '240px' }}>
                上班：
                <Select
                  style={{ width: '190px' }}
                  value={fixedRule.onworkEffectiveStart}
                  onChange={(value) =>
                    setFixedRule({ ...fixedRule, onworkEffectiveStart: value })
                  }
                >
                  {CLOCK_IN_OPTIONS.map((option) => (
                    <Option value={option.value} key={option.value}>
                      {option.onWorkText}
                    </Option>
                  ))}
                </Select>
              </span>
              <span className="rule-item" style={{ width: '240px' }}>
                下班：
                <Select
                  style={{ width: '190px' }}
                  value={fixedRule.offworkEffectiveEnd}
                  onChange={(value) =>
                    setFixedRule({ ...fixedRule, offworkEffectiveEnd: value })
                  }
                >
                  {CLOCK_IN_OPTIONS.map((option) => (
                    <Option value={option.value} key={option.value}>
                      {option.offWorkText}
                    </Option>
                  ))}
                </Select>
              </span>
            </h4>
          </div>
        </div>
        <div className="actions">
          <div>
            <Button onClick={handleCancel}>取消</Button>
            <Button
              type="primary"
              onClick={handleCheckRules}
              disabled={(fixedRule.timeRules || []).some(
                (rule) => rule.startTime === null || rule.endTime === null
              )}
            >
              确定
            </Button>
          </div>
        </div>
      </div>
    )
  }, [
    fixedRule,
    handleCreateValidateErrorProps,
    handleStartTimeChange,
    handleEndTimeChange,
    handleCancel,
    handleCheckRules
  ])

  // 灵活考勤设置
  const renderFlexibleSetting = useCallback(() => {
    const addTimeRule = () =>
      setFlexibleRule({
        ...flexibleRule,
        timeRules: [
          ...flexibleRule.timeRules,
          {
            startTime: null,
            endTime: null,
            num: flexibleRule.timeRules.length + 1
          }
        ]
      })
    const deleteTimeRule = (index: number) => {
      const newTimeRules = flexibleRule.timeRules.filter((_, i) => i !== index)
      setFlexibleRule({ ...flexibleRule, timeRules: newTimeRules })
    }
    const lastRule = flexibleRule.timeRules[flexibleRule.timeRules.length - 1]

    return (
      <div>
        <div className="table">
          <div className="row row-gray">
            <h4>有效打卡时间范围</h4>
          </div>
          <Form layout="inline">
            {flexibleRule.timeRules.map((rule, index) => {
              const validateErrorProps = handleCreateValidateErrorProps(index)
              return (
                <div
                  className={classnames(
                    'row',
                    { 'row-gray': index % 2 === 1 },
                    { 'row-white': index % 2 === 0 }
                  )}
                  key={index}
                >
                  <FormItem
                    className="rule-item"
                    label="开始"
                    {...validateErrorProps.start}
                  >
                    <TimePicker
                      placeholder="选择时间"
                      allowClear={false}
                      format={CONFIG.hourMinuteFormat}
                      value={
                        rule.startTime !== null
                          ? dayjs().startOf('day').minute(rule.startTime)
                          : null
                      }
                      onChange={(time) =>
                        handleStartTimeChange(index, time)
                      }
                    />
                    {rule.startTime !== null && rule.startTime >= 24 * 60 && <Tag color="gold">次日</Tag>}
                  </FormItem>
                  <FormItem
                    className="rule-item"
                    label="结束"
                    {...validateErrorProps.end}
                  >
                    <TimePicker
                      placeholder="选择时间"
                      allowClear={false}
                      format={CONFIG.hourMinuteFormat}
                      value={
                        rule.endTime !== null
                          ? dayjs().startOf('day').minute(rule.endTime)
                          : null
                      }
                      onChange={(time) =>
                        handleEndTimeChange(index, time)
                      }
                    />
                    {rule.endTime !== null && rule.endTime >= 24 * 60 && <Tag color="gold">次日</Tag>}
                  </FormItem>
                  {index > 0 && (
                    <Popconfirm
                      title="是否确认删除？"
                      onConfirm={() => deleteTimeRule(index)}
                    >
                      <span className="remove">
                        <CloseCircleOutlined />
                      </span>
                    </Popconfirm>
                  )}
                </div>
              )
            })}
          </Form>
          {flexibleRule.timeRules.length < 5 && (
            <div
              className={classnames(
                'row',
                { 'row-gray': flexibleRule.timeRules.length % 2 === 1 },
                { 'row-white': flexibleRule.timeRules.length % 2 === 0 }
              )}
            >
              <Button
                type="link"
                onClick={addTimeRule}
                disabled={
                  lastRule?.startTime === null || lastRule?.endTime === null
                }
              >
                <PlusOutlined />
                时间范围
              </Button>
            </div>
          )}
        </div>
        <div className="table">
          <div className="row row-gray">
            <h4>
              工作时长要求：
              <TimePicker
                style={{ width: '170px' }}
                placeholder="无要求"
                format="HH小时mm分钟"
                allowClear={false}
                value={
                  flexibleRule.minDuration
                    ? dayjs().startOf('day').minute(flexibleRule.minDuration)
                    : null
                }
                onChange={(time) =>
                  time && setFlexibleRule({
                    ...flexibleRule,
                    minDuration: time.diff(dayjs().startOf('day'), 'minutes')
                  })
                }
              />
            </h4>
          </div>
        </div>
        <div className="actions">
          <div>
            <Button onClick={handleCancel}>取消</Button>
            <Button
              type="primary"
              onClick={handleCheckRules}
              disabled={(flexibleRule.timeRules || []).some(
                (rule) => rule.startTime === null || rule.endTime === null
              )}
            >
              确定
            </Button>
          </div>
        </div>
      </div>
    )
  }, [
    flexibleRule,
    handleCreateValidateErrorProps,
    handleStartTimeChange,
    handleEndTimeChange,
    handleCancel,
    handleCheckRules
  ])

  // 渲染规则设置
  const renderRuleSetting = useCallback(() => {
    return (
      <div className="setting">
        <div className="title">
          <h3>
            <span>|</span>设置考勤规则
          </h3>
        </div>
        <div className="types">
          <Radio.Group value={ruleType}>
            <div
              className={classnames('type', {
                active: ruleType === ATTENDANCE_TYPE.FIXED
              })}
              onClick={() => setRuleType(ATTENDANCE_TYPE.FIXED)}
            >
              <Radio value={ATTENDANCE_TYPE.FIXED}>
                <span style={{ color: 'rgba(0,0,0,0.85)', fontSize: '16px' }}>
                  固定时间上下班
                </span>
              </Radio>
              <p>有固定的上下班时间，考核迟到和早退</p>
            </div>
            <div
              className={classnames('type', {
                active: ruleType === ATTENDANCE_TYPE.FLEXIBLE
              })}
              onClick={() => setRuleType(ATTENDANCE_TYPE.FLEXIBLE)}
            >
              <Radio value={ATTENDANCE_TYPE.FLEXIBLE}>
                <span style={{ color: 'rgba(0,0,0,0.85)', fontSize: '16px' }}>
                  灵活时间上下班
                </span>
              </Radio>
              <p>上下班时间不固定，考核工作时长</p>
            </div>
          </Radio.Group>
        </div>
        {ruleType === ATTENDANCE_TYPE.FLEXIBLE && renderFlexibleSetting()}
        {ruleType === ATTENDANCE_TYPE.FIXED && renderFixedSetting()}
      </div>
    )
  }, [ruleType, renderFlexibleSetting, renderFixedSetting])

  // 渲染内容
  const renderContent = useCallback(() => {
    if (isEdit) {
      return renderRuleSetting()
    } else {
      if (ruleType === ATTENDANCE_TYPE.FLEXIBLE) {
        return renderFlexibleRule()
      }
      return renderFixedRule()
    }
  }, [isEdit, ruleType, renderRuleSetting, renderFlexibleRule, renderFixedRule])

  return (
    <Layout className="Attendance-V3">
      <PageHeader
        ghost={false}
        style={{ borderBottom: '1px solid rgb(235, 237, 240)' }}
        title="考勤设置"
      />
      <Content className="content">{renderContent()}</Content>
    </Layout>
  )
}

export { Setting }
export default Setting
